import _taggedTemplateLiteral from "@babel/runtime/helpers/taggedTemplateLiteral";
var _templateObject, _templateObject2, _templateObject3, _templateObject4;
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useLayoutEffect, useMemo } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { useIntl } from 'react-intl-next';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import { IconFallback } from '@atlaskit/editor-common/quick-insert';
import { SelectItemMode, typeAheadListMessages } from '@atlaskit/editor-common/type-ahead';
import { relativeFontSizeToBase16 } from '@atlaskit/editor-shared-styles';
import { shortcutStyle } from '@atlaskit/editor-shared-styles/shortcut';
import { ButtonItem } from '@atlaskit/menu';
import { B400, N30, N800 } from '@atlaskit/theme/colors';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import VisuallyHidden from '@atlaskit/visually-hidden';
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export var itemIcon = css({
  width: "var(--ds-space-500, 40px)",
  height: "var(--ds-space-500, 40px)",
  overflow: 'hidden',
  border: "var(--ds-border-width, 1px)".concat(" solid ", "var(--ds-border, rgba(223, 225, 229, 0.5))") /* N60 at 50% */,
  borderRadius: "var(--ds-radius-small, 3px)",
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
  div: {
    width: "var(--ds-space-500, 40px)",
    height: "var(--ds-space-500, 40px)"
  }
});
var itemIconSizeUpdated = css({
  width: "var(--ds-space-400, 32px)",
  height: "var(--ds-space-400, 32px)",
  // AI icons may contain div as container of the icon
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
  div: {
    width: "var(--ds-space-400, 32px)",
    height: "var(--ds-space-400, 32px)"
  }
});

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
var itemBody = css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n\tdisplay: flex;\n\tflex-direction: row;\n\tflex-wrap: nowrap;\n\tjustify-content: space-between;\n"])));
var itemText = css({
  whiteSpace: 'initial',
  color: "".concat("var(--ds-text, ".concat(N800, ")"))
});
var itemTitle = css({
  // eslint-disable-next-line @atlaskit/design-system/use-tokens-typography
  lineHeight: '1.4'
});
var itemTitleOverride = css({
  font: "var(--ds-font-body, normal 400 14px/20px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)"
});
var itemDescription = css({
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/design-system/use-tokens-typography
  fontSize: "".concat(relativeFontSizeToBase16(12), ";"),
  color: "var(--ds-text-subtle, #505258)".concat(";"),
  marginTop: "var(--ds-space-050, 4px)".concat(";")
});
var itemDescriptionOverride = css({
  font: "var(--ds-font-body-small, normal 400 11px/16px ui-sans-serif, -apple-system, BlinkMacSystemFont, \"Segoe UI\", Ubuntu, \"Helvetica Neue\", sans-serif)",
  marginTop: 0
});

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression, @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
var itemAfter = css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n\tflex: 0 0 auto;\n"])));

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Ignored via go/DSP-18766
var customRenderItemDivStyle = css(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n\toverflow: hidden;\n\t&:focus {\n\t\tbox-shadow: inset 2px 0px 0px ", ";\n\t\tbackground-color: ", ";\n\t\toutline: none;\n\t}\n"])), "var(--ds-border-focused, ".concat(B400, ")"), "var(--ds-background-neutral-subtle-hovered, ".concat(N30, ")"));

/**
 * This CSS emulates the desired behaviour with :focus-visible for firefox.
 * Firefox unfortunately does not register keyboard focus if user mouseDown and drag a typeahead item
 * resulting in focus-visible style not drawn.
 */
var selectionFrame = {
  '& > button:focus': {
    boxShadow: "inset 2px 0px 0px ".concat("var(--ds-border-focused, ".concat(B400, ")"), ";"),
    backgroundColor: "".concat("var(--ds-background-neutral-subtle-hovered, ".concat(N30, ")")),
    outline: 'none',
    '&:active': {
      boxShadow: 'none'
    }
  },
  '& > button:hover': {
    backgroundColor: 'inherit',
    outline: 'none'
  }
};

// eslint-disable-next-line @atlaskit/design-system/no-css-tagged-template-expression -- Ignored via go/DSP-18766
var selectedStyle = css(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n\tbackground-color: ", ";\n\tbox-shadow: inset 2px 0px 0px ", ";\n"])), "var(--ds-background-neutral-subtle-hovered, ".concat(N30, ")"), "var(--ds-border-focused, ".concat(B400, ")"));
var disabledStyle = css({
  color: "var(--ds-text-disabled, #080F214A)"
});
var titleWithLozengeStyle = css({
  display: 'flex',
  alignItems: 'center',
  gap: "var(--ds-space-050, 4px)"
});
var FallbackIcon = /*#__PURE__*/React.memo(function (_ref) {
  var label = _ref.label;
  return jsx(IconFallback, null);
});
var noop = function noop() {};
var CustomItemComponentWrapper = /*#__PURE__*/React.memo(function (props) {
  var customRenderItem = props.customRenderItem,
    isSelected = props.isSelected,
    itemIsDisabled = props.itemIsDisabled,
    ariaLabel = props.ariaLabel,
    itemsLength = props.itemsLength,
    customItemRef = props.customItemRef,
    insertSelectedItem = props.insertSelectedItem,
    itemIndex = props.itemIndex;
  var Comp = customRenderItem;
  var listItemClasses = useMemo(function () {
    return [customRenderItemDivStyle, isSelected && !itemIsDisabled && selectedStyle, itemIsDisabled && disabledStyle];
  }, [isSelected, itemIsDisabled]);
  return jsx("div", {
    "aria-selected": isSelected,
    role: "option",
    "aria-label": ariaLabel,
    "aria-setsize": itemsLength,
    "aria-posinset": expValEquals('platform_editor_fix_a11y_aria_posinset_0', 'isEnabled', true) ? itemIndex + 1 : itemIndex,
    tabIndex: 0
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    ,
    css: listItemClasses
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
    ,
    className: "ak-typeahead-item ".concat(isSelected ? 'typeahead-selected-item' : '')
    //CSS classes added for test cases purpose
    ,
    ref: customItemRef
  }, jsx("div", {
    "aria-hidden": true
  }, jsx(Comp, {
    onClick: insertSelectedItem,
    isSelected: false //The selection styles are handled in the parent div instead. Hence isSelected is made false always.
    ,
    onHover: noop
  })));
});
export var TypeAheadListItem = /*#__PURE__*/React.memo(function (_ref2) {
  var item = _ref2.item,
    itemsLength = _ref2.itemsLength,
    selectedIndex = _ref2.selectedIndex,
    onItemClick = _ref2.onItemClick,
    itemIndex = _ref2.itemIndex,
    ariaLabel = _ref2.ariaLabel,
    moreElementsInQuickInsertViewEnabled = _ref2.moreElementsInQuickInsertViewEnabled,
    api = _ref2.api,
    firstOnlineSupportedIndex = _ref2.firstOnlineSupportedIndex;
  var _useSharedPluginState = useSharedPluginStateWithSelector(api, ['connectivity'], function (states) {
      var _states$connectivityS;
      return {
        connectivityMode: (_states$connectivityS = states.connectivityState) === null || _states$connectivityS === void 0 ? void 0 : _states$connectivityS.mode
      };
    }),
    connectivityMode = _useSharedPluginState.connectivityMode;
  var isItemDisabled = function isItemDisabled(item) {
    var _item$isDisabledOffli;
    return connectivityMode === 'offline' && ((_item$isDisabledOffli = item === null || item === void 0 ? void 0 : item.isDisabledOffline) !== null && _item$isDisabledOffli !== void 0 ? _item$isDisabledOffli : false);
  };
  var itemIsDisabled = isItemDisabled(item);
  var isFirstEnabledIndex = connectivityMode === 'offline' && itemIndex === firstOnlineSupportedIndex && selectedIndex === -1;

  /**
   * To select and highlight the first Item when no item is selected
   * However selectedIndex remains -1, So that user does not skip the first item when down arrow key is used from typeahead query(inputQuery.tsx)
   */
  var isSelected = false;
  // Feature gated - connectivity is only available on desktop and behind a feature gate on full page mode
  if (connectivityMode === undefined) {
    isSelected = itemIndex === selectedIndex || selectedIndex === -1 && itemIndex === 0;
  } else {
    isSelected = itemIndex === selectedIndex || selectedIndex === -1 && (itemIndex === 0 || isFirstEnabledIndex) && !itemIsDisabled;
  }

  // Assistive text
  var intl = useIntl();
  var descriptionText = item.description ? "".concat(item.description, ".") : '';
  var shortcutText = item.keyshortcut ? " ".concat(intl.formatMessage(typeAheadListMessages.shortcutLabel), " ").concat(item.keyshortcut, ".") : '';
  var descriptionId = "typeahead-item-description-".concat(itemIndex);
  var icon = item.icon,
    title = item.title,
    customRenderItem = item.render;
  var elementIcon = useMemo(function () {
    return jsx("div", {
      css: [itemIcon, moreElementsInQuickInsertViewEnabled && itemIconSizeUpdated]
    }, icon ? icon() : jsx(FallbackIcon, {
      label: title
    }));
  }, [icon, title, moreElementsInQuickInsertViewEnabled]);
  var insertSelectedItem = useCallback(function () {
    if (itemIsDisabled) {
      return;
    }
    onItemClick(SelectItemMode.SELECTED, itemIndex);
  }, [onItemClick, itemIndex, itemIsDisabled]);
  var customItemRef = React.useRef(null);
  var buttonItemRef = React.useRef(null);
  var shouldUpdateFocus = selectedIndex === itemIndex && !isFirstEnabledIndex;
  var listItemClasses = useMemo(function () {
    return [selectionFrame, isSelected && !itemIsDisabled && selectedStyle];
  }, [isSelected, itemIsDisabled]);
  useLayoutEffect(function () {
    if (shouldUpdateFocus) {
      var _customItemRef$curren;
      customItemRef === null || customItemRef === void 0 || (_customItemRef$curren = customItemRef.current) === null || _customItemRef$curren === void 0 || _customItemRef$curren.focus();
    }
  }, [customItemRef, shouldUpdateFocus]);
  useLayoutEffect(function () {
    if (shouldUpdateFocus) {
      var _buttonItemRef$curren;
      buttonItemRef === null || buttonItemRef === void 0 || (_buttonItemRef$curren = buttonItemRef.current) === null || _buttonItemRef$curren === void 0 || _buttonItemRef$curren.focus();
    }
  }, [buttonItemRef, shouldUpdateFocus]);
  if (customRenderItem) {
    return jsx(CustomItemComponentWrapper, {
      itemIsDisabled: itemIsDisabled,
      customRenderItem: customRenderItem,
      isSelected: isSelected,
      ariaLabel: ariaLabel,
      itemsLength: itemsLength,
      customItemRef: customItemRef,
      insertSelectedItem: insertSelectedItem,
      itemIndex: itemIndex
    });
  }
  return (
    // eslint-disable-next-line @atlaskit/design-system/consistent-css-prop-usage -- Ignored via go/DSP-18766
    jsx("span", {
      css: listItemClasses
    }, (descriptionText || shortcutText) && jsx(VisuallyHidden, {
      id: descriptionId
    }, descriptionText, " ", shortcutText), jsx(ButtonItem, {
      onClick: insertSelectedItem,
      iconBefore: elementIcon,
      isSelected: isSelected,
      "aria-selected": isSelected,
      "aria-label": title,
      "aria-describedby": descriptionText || shortcutText ? descriptionId : undefined,
      "aria-setsize": itemsLength,
      "aria-posinset": expValEquals('platform_editor_fix_a11y_aria_posinset_0', 'isEnabled', true) ? itemIndex + 1 : itemIndex,
      role: "option",
      ref: buttonItemRef,
      isDisabled: itemIsDisabled
      // @ts-ignore
      ,
      css: listItemClasses
    }, jsx("div", {
      "aria-hidden": true
    }, jsx("div", {
      css: [itemText]
    }, jsx("div", {
      css: itemBody
    }, jsx("div", {
      css: [itemTitle, moreElementsInQuickInsertViewEnabled && itemTitleOverride, itemIsDisabled && disabledStyle, item.lozenge && expValEquals('platform_synced_block', 'isEnabled', true) && titleWithLozengeStyle]
    }, item.title, expValEquals('platform_synced_block', 'isEnabled', true) && item.lozenge), jsx("div", {
      css: itemAfter
    }, item.keyshortcut && jsx("div", {
      css: shortcutStyle
    }, item.keyshortcut))), jsx("div", {
      css: [itemDescription, moreElementsInQuickInsertViewEnabled && itemDescriptionOverride, itemIsDisabled && disabledStyle]
    }, item.description)))))
  );
});